/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import user from 'js/lib/user';

import { Grid, Link, PageGridContainer, Typography2, useTheme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';
import { Breadcrumb } from '@coursera/coursera-ui';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import Skeleton from 'bundles/program-home/components/cds/Skeleton';
import type {
  PropsForWithHasMultipleProgramProps,
  PropsFromWithHasMultipleProgramProps,
} from 'bundles/program-home/components/multiprogram/withHasMultipleProgramsProp';
import withHasMultipleProgramProps from 'bundles/program-home/components/multiprogram/withHasMultipleProgramsProp';
import { COLLECTION_TYPE } from 'bundles/program-home/components/single-program/SingleProgramConstants';

import _t from 'i18n!nls/program-home';

type PropsFromCaller = {
  programSlug: string;
  thirdPartyOrgSlug: string;
  thirdPartyOrganizationId: string;
  collectionType: COLLECTION_TYPE;
  programName: string;
  title?: string;
  loading?: boolean;
};

type Props = PropsFromCaller;

export const useStyles = () => {
  const theme = useTheme();
  return {
    container: css`
      background-color: ${theme.palette.gray[100]};
      display: grid;
      width: 100%;

      .breadcrumb {
        margin: 0 auto;
        padding: ${theme.spacing(12, 0)};
      }
    `,
    separator: css`
      margin: ${theme.spacing(0, 8)};
      display: block;
    `,
    gridContainer: css`
      padding-top: 0 !important; /* remove padding from CDS Grid */
    `,
    gridItem: css`
      width: 100%;
    `,
  };
};

type PropsForHomeLink = {
  thirdPartyOrgSlug: string;
} & PropsForWithHasMultipleProgramProps;

const MaybeHomeLinkFC = ({
  thirdPartyOrgSlug,
  hasMultipleProgramsInOrg,
  ...breadcrumbItemProps
}: PropsForHomeLink & PropsFromWithHasMultipleProgramProps) =>
  hasMultipleProgramsInOrg ? (
    <Breadcrumb.Item {...breadcrumbItemProps} htmlAttributes={{ role: 'listitem' }}>
      <Link
        variant="quiet"
        data={{ thirdPartyOrgSlug }}
        trackingName="program_home_collections_home_breadcrumb_link"
        component={TrackedLink2}
        href={`/programs/all/${thirdPartyOrgSlug}`}
      >
        {_t('All Learning Programs')}
      </Link>
    </Breadcrumb.Item>
  ) : null;

const MaybeHomeLink = compose<PropsForHomeLink & PropsFromWithHasMultipleProgramProps, PropsForHomeLink>(
  // TODO(ppaskaris): Remove after multi-program experiment
  withHasMultipleProgramProps
)(MaybeHomeLinkFC);

export const CollectionsBreadcrumbs = ({
  programSlug,
  collectionType,
  programName,
  thirdPartyOrgSlug,
  thirdPartyOrganizationId,
  title,
  loading = false,
}: Props): JSX.Element => {
  const styles = useStyles();

  const COLLECTION_TITLE = {
    [COLLECTION_TYPE.CURATED]: _t('All recommendations by your organization'),
    [COLLECTION_TYPE.BROWSE]: _t('All recommendations by Coursera'),
  };

  return (
    <div css={styles.container}>
      <PageGridContainer css={styles.gridContainer}>
        <Grid item xs={12} css={styles.gridItem}>
          <Typography2 component="h4" variant="subtitleMedium" color="supportText">
            <Breadcrumb
              separatorClassName="separator"
              separator={<ChevronNextIcon size="small" css={styles.separator} />}
              rootClassName="breadcrumb"
              htmlAttributes={{ role: 'list' }}
            >
              <MaybeHomeLink
                thirdPartyOrgSlug={thirdPartyOrgSlug}
                thirdPartyOrganizationId={thirdPartyOrganizationId}
                userId={user.get().id}
              />
              <Breadcrumb.Item htmlAttributes={{ role: 'listitem' }}>
                <Link
                  variant="quiet"
                  data={{ programSlug, collectionType }}
                  trackingName="program_home_collections_program_breadcrumb_link"
                  href={`/programs/${programSlug}`}
                  component={TrackedLink2}
                >
                  {programName}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item htmlAttributes={{ role: 'listitem' }}>
                {loading ? <Skeleton fixedWidth={150} /> : <>{title || COLLECTION_TITLE[collectionType]}</>}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Typography2>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default CollectionsBreadcrumbs;
